import React, { useState } from "react";
import { useParams } from "react-router-dom";

import Button from "@cayuse/shared-react.components.button";
import ButtonGroup from "@cayuse/shared-react.components.button-group";
import IconButton from "@cayuse/shared-react.components.icon-button";
import Modal from "@cayuse/shared-react.components.modal";
import { isNonEmptyArray } from "@cayuse/shared-react.utils.type-checks";
import PropTypes from "prop-types";
import { api } from "services";

import SectionPicker from "components/StartNewAward/SectionPicker";
import {
	AWARDS_BASE_ROUTE,
	ERROR_NOT_AUTHORIZED,
	ERROR_STATUS_CODE_400,
} from "globals/constants";
import { useDictionary } from "hooks";
import { refreshAwards } from "store/actions/awardList";
import { refreshProjects } from "store/actions/projectList";
import { refreshProposals } from "store/actions/proposals";
import { fetchShell } from "store/actions/shell";
import {
	spUrl,
	notifyError,
} from "utils";
import history from "utils/history";

import styles from "./styles.css";


const StartNewSubawardFromAward = ({ parentId }) => {
	const defaults = {
		visible: false,
		open: false,
		transitionDuration: 150,
		titleField: "",
		searchField: undefined,
		confirmDisabled: true
	};

	const dictionary = useDictionary("awards.startNewSubaward");

	const [ isLoading, setIsLoading ] = useState(false);
	const [ open, setOpen ] = useState(defaults.open);
	const [ checkedValues, setCheckedValues ] = useState([]);
	const [ formElements, setFormElements ] = useState([]);

	const { awardId } = useParams();

	const refreshData = () => {
		fetchShell();
		refreshProposals();
		refreshProjects();
		refreshAwards();
	};

	const onClose = () => {
		setOpen(false);
	};

	const createSubaward = async payload => {
		try {
			setIsLoading(true);

			const response = await api.subaward.create(awardId, payload);
			const subawardId = response.subawardId;

			refreshData();
			onClose();
			history.push(spUrl(`${AWARDS_BASE_ROUTE}/${awardId}/sub-awards/${subawardId}`));
		} catch (error) {
			setIsLoading(false);

			if (error.response.status === ERROR_STATUS_CODE_400) {
				notifyError(dictionary.errors.notAuthorized || ERROR_NOT_AUTHORIZED, error);
			} else {
				notifyError(dictionary.errors.default || "Failed to copy data from form", error);
			}
		}
	};

	const createSubawardFromSource = async () => {
		const headerIds = checkedValues.reduce((headerIds, section) => {
			return [ ...headerIds, ...section.fieldHeaderIds ];
		}, []);
		const payload = {
			copyFields: headerIds
		};

		const duplicateHeaderIds = headerIds.filter((item, index) => headerIds.indexOf(item) !== index);
		const hasDuplicateHeaderIds = isNonEmptyArray(duplicateHeaderIds);
		const elementsWithDuplicateHeaderIds = formElements.filter(element => (
			duplicateHeaderIds.includes(element.headerId)
		));

		const formattedErrorMessage = () => {
			return (
				<>
					<p>
						{/* eslint-disable-next-line max-len */}
						Duplicate Labels - Unselect sections that contain duplicate labels to copy data from remaining selections.Remove duplicate Labels from the form to prevent this error from occurring on future forms.
					</p>
					<div>

					</div>
					<ul className={styles.errorList}>
						{elementsWithDuplicateHeaderIds.map(element => {
							return (
								<li>
									<p><strong>Section:</strong> {element.section}</p>
									<p><strong>Question:</strong> {element.label}</p>
									<p><strong>Label:</strong> {element.headerId}</p>
								</li>
							);
						})}
					</ul>
				</>
			);
		};

		if (hasDuplicateHeaderIds) {
			notifyError(formattedErrorMessage());
			setIsLoading(false);
		} else if (headerIds.some(headerId => headerId === null)) {
			notifyError(
				dictionary.errors.missingLabel ||
				"One or more labels are missing on the form you are trying to copy from. Copying from this form will not work",
			);
			setIsLoading(false);
		} else {
			createSubaward(payload);
		}
	};

	// Remove onCreateSubaward and replace trigger onclick with resetModalValues on the modal trigger when copy from award is scopped.
	const onCreateSubaward = async () => {
		createSubaward({});
	};

	return (
		<Modal
			className={styles.modal}
			closeIcon
			trigger={(
				<IconButton
					icon="plus"
					labelPosition="left"
					primary
					onClick={onCreateSubaward} // Replace with resetModalValues when copy from award is scoped.
					disabled={isLoading}
					busy={isLoading}
				>
					{ dictionary.startNew || "Add A Subaward" }
				</IconButton>
			)}
			open={open}
			onClose={onClose}
			size="one-half"
			ariaLabel={dictionary.selectSections || "Select sections to copy"}
		>
			<Modal.Header className="ui header whiteOnBlue">
				<h2>{ dictionary.selectSections || "Select sections to copy" }</h2>
			</Modal.Header>
			<Modal.Content>
				<SectionPicker
					parentId={parentId}
					parentType={"award"}
					headerText="All selected sections will be copied to the new subaward" /* todo: needs to go to the modal and into the dictionary */
					checkedValues={checkedValues}
					setCheckedValues={setCheckedValues}
					setFormElements={setFormElements}
				/>
			</Modal.Content>
			<Modal.Actions>
				<div className="field">
					<ButtonGroup>
						<Button secondary onClick={onClose}>{dictionary.cancel || "Cancel"}</Button>
						<Button className={styles.confirmButton}
							onClick={createSubawardFromSource}
							disabled={isLoading}
							busy={isLoading}
							loadingMessage={dictionary.confirmNewSubawardAria || "Creating Subaward"}
							tooltip={isLoading && (dictionary.disabledReason || "Loading Sections For Selection")}
						>
							{dictionary.confirmNewSubaward || "Create Subaward"}
						</Button>
					</ButtonGroup>
				</div>
			</Modal.Actions>
		</Modal>
	);
};

StartNewSubawardFromAward.propTypes = {
	parentId: PropTypes.string.isRequired
};

export default StartNewSubawardFromAward;
